/**
 * frameworkCatalog.js — S9 (ADR-S9-005)
 *
 * Copy estático curado de descripción + valor por framework para la página pública /normas.
 * IDs = los mismos de constants/frameworks.js (fuente de verdad de labels/colores/grupos).
 *
 * Reglas de honestidad (§6.1 del tech-design):
 *  - Los 4 core IT (iso27001, nist_scf, cis_v8, iso20000) y los 3 NORTIC tienen catálogo
 *    de controles bilingüe completo → se presentan como integrados.
 *  - nist_csf_2, iso_31000, iso_22301 son destinos de CROSS-MAPPING, no catálogos de
 *    controles gestionados. Se etiquetan como "referencia" con isReference=true.
 *  - Los 6 Zona Franca tienen catálogos de controles propios (OEA 20, CNZFE 24, etc.).
 *
 * El valor está verificado contra las capacidades reales del backend:
 *  gap analysis (server.py:5004), readiness (:5486), templates (:1562),
 *  cross-mappings (db.control_mappings, :2890), benchmarking (:6243), wizard (:5350).
 */

export const FRAMEWORK_CATALOG = {
  // ============================================================
  // CORE IT — catálogo de controles bilingüe, integración completa
  // ============================================================

  iso27001: {
    isReference: false,
    description: {
      es: "La norma internacional de gestión de seguridad de la información. Define los requisitos para establecer, implementar, mantener y mejorar continuamente un SGSI (Sistema de Gestión de Seguridad de la Información) basado en el Anexo A con 93 controles.",
      en: "The international standard for information security management. Defines requirements to establish, implement, maintain, and continually improve an ISMS based on Annex A with 93 controls.",
    },
    value: {
      es: [
        "Análisis de brechas multi-marco con puntuación de cumplimiento en tiempo real",
        "Declaración de Aplicabilidad (SoA) con los 93 controles del Anexo A gestionados",
        "Mapeo cruzado automático ISO 27001 ↔ NIST SCF ↔ CIS v8.1 ↔ NORTIC",
        "Puntaje de preparación para certificación (Readiness Score) con hoja de ruta",
        "Generación de políticas con IA: SGSI, Control de Acceso, Gestión de Incidentes y más",
        "Registro de riesgos con metodología ISO 27005 integrada",
        "Catálogo de controles bilingüe (ES/EN) con traducciones curadas",
      ],
      en: [
        "Multi-framework gap analysis with real-time compliance scoring",
        "Statement of Applicability (SoA) managing all 93 Annex A controls",
        "Automatic cross-mapping ISO 27001 ↔ NIST SCF ↔ CIS v8.1 ↔ NORTIC",
        "Certification Readiness Score with actionable roadmap",
        "AI-powered policy generation: ISMS, Access Control, Incident Management, and more",
        "Risk register with integrated ISO 27005 methodology",
        "Bilingual control catalog (ES/EN) with curated translations",
      ],
    },
  },

  nist_scf: {
    isReference: false,
    description: {
      es: "El NIST Secure Controls Framework (SCF) es un meta-marco de ciberseguridad que consolida más de 100 estándares y regulaciones en un catálogo unificado de controles. Ideal para organizaciones que necesitan demostrar cumplimiento simultáneo con múltiples marcos.",
      en: "The NIST Secure Controls Framework (SCF) is a cybersecurity meta-framework that consolidates 100+ standards and regulations into a unified control catalog. Ideal for organizations needing to demonstrate simultaneous compliance with multiple frameworks.",
    },
    value: {
      es: [
        "Catálogo SCF completo con mapeos a ISO 27001, CIS v8.1 e ISO 20000",
        "Análisis de brechas sobre el meta-marco — un gap, múltiples certificaciones",
        "Implementación guiada paso a paso con el Asistente IA",
        "Plantillas de políticas y procedimientos alineadas al SCF",
        "Benchmarking de postura de cumplimiento frente a pares de la industria",
        "Integración con evidencia cloud (AWS, Azure, GCP) para controles técnicos",
      ],
      en: [
        "Full SCF catalog with mappings to ISO 27001, CIS v8.1, and ISO 20000",
        "Gap analysis on the meta-framework — one gap, multiple certifications",
        "Step-by-step guided implementation with the AI Assistant",
        "Policy and procedure templates aligned to SCF",
        "Compliance posture benchmarking against industry peers",
        "Cloud evidence integration (AWS, Azure, GCP) for technical controls",
      ],
    },
  },

  cis_v8: {
    isReference: false,
    description: {
      es: "Los CIS Controls v8.1 son un conjunto de 18 Grupos de Controles y 153 Salvaguardas priorizadas por impacto defensivo. Ampliamente adoptados como base de ciberhigiene para organizaciones de todo tamaño.",
      en: "CIS Controls v8.1 is a set of 18 Control Groups and 153 Safeguards prioritized by defensive impact. Widely adopted as the cybersecurity hygiene baseline for organizations of all sizes.",
    },
    value: {
      es: [
        "Los 153 Salvaguardas catalogadas con nivel de implementación (IG1/IG2/IG3)",
        "Mapeo cruzado CIS ↔ ISO 27001 ↔ NIST SCF — controles compartidos sin duplicación",
        "Análisis de brechas priorizado: primero las Salvaguardas IG1 (base de ciberhigiene)",
        "Recolección de evidencia automática desde conectores cloud para Salvaguardas técnicas",
        "Readiness Score para auditorías CIS CAT o certificaciones de ciberseguridad",
        "Predictor de riesgo IA basado en estado de implementación de controles CIS",
      ],
      en: [
        "All 153 Safeguards cataloged with implementation group level (IG1/IG2/IG3)",
        "Cross-mapping CIS ↔ ISO 27001 ↔ NIST SCF — shared controls without duplication",
        "Prioritized gap analysis: IG1 Safeguards first (cybersecurity hygiene baseline)",
        "Automatic evidence collection from cloud connectors for technical Safeguards",
        "Readiness Score for CIS CAT audits or cybersecurity certifications",
        "AI risk predictor based on CIS control implementation status",
      ],
    },
  },

  iso20000: {
    isReference: false,
    description: {
      es: "ISO 20000-1 es el estándar internacional para Sistemas de Gestión de Servicios de TI (ITSM). Define los requisitos para entregar servicios de TI gestionados de forma controlada y continua, alineado a ITIL.",
      en: "ISO 20000-1 is the international standard for IT Service Management Systems (ITSM). Defines requirements for delivering managed IT services in a controlled and continual manner, aligned with ITIL.",
    },
    value: {
      es: [
        "Catálogo de controles ISO 20000 con Declaración de Aplicabilidad (SoA) propia",
        "Mapeo cruzado con ISO 27001 para organizaciones con ambas certificaciones",
        "Gestión de incidentes y acciones correctivas alineada a los requisitos del estándar",
        "Plantillas de políticas ITSM: Gestión de Niveles de Servicio, Capacidad, Continuidad",
        "Panel de cumplimiento con métricas de servicios en tiempo real",
        "Análisis de brechas específico para organizaciones que persiguen certificación ISO 20000",
      ],
      en: [
        "ISO 20000 control catalog with its own Statement of Applicability (SoA)",
        "Cross-mapping with ISO 27001 for organizations pursuing dual certification",
        "Incident management and corrective actions aligned to standard requirements",
        "ITSM policy templates: Service Level Management, Capacity, Continuity",
        "Compliance dashboard with real-time service metrics",
        "Gap analysis specific to organizations pursuing ISO 20000 certification",
      ],
    },
  },

  // ============================================================
  // MARCOS DE REFERENCIA — destinos de cross-mapping, no catálogo completo gestionado
  // ============================================================

  nist_csf_2: {
    isReference: true,
    description: {
      es: "NIST CSF 2.0 (2024) es el framework de ciberseguridad del NIST actualizado con 6 funciones: Gobernar, Identificar, Proteger, Detectar, Responder y Recuperar. Usado como destino de cross-mapping para alinear controles ISO 27001 y NIST SCF.",
      en: "NIST CSF 2.0 (2024) is the NIST cybersecurity framework updated with 6 functions: Govern, Identify, Protect, Detect, Respond, and Recover. Used as a cross-mapping target to align ISO 27001 and NIST SCF controls.",
    },
    value: {
      es: [
        "Cross-mapping automático: tus controles ISO 27001/SCF mapeados a las funciones CSF 2.0",
        "Visualización de cobertura por función (Gobernar, Identificar, Proteger, Detectar, Responder, Recuperar)",
        "Identificación de brechas de cobertura respecto al CSF 2.0 sin gestión duplicada",
        "Marco de referencia para comunicar postura de ciberseguridad a stakeholders ejecutivos",
      ],
      en: [
        "Automatic cross-mapping: your ISO 27001/SCF controls mapped to CSF 2.0 functions",
        "Coverage visualization per function (Govern, Identify, Protect, Detect, Respond, Recover)",
        "Coverage gap identification against CSF 2.0 without duplicate management",
        "Reference framework for communicating cybersecurity posture to executive stakeholders",
      ],
    },
  },

  iso_31000: {
    isReference: true,
    description: {
      es: "ISO 31000:2018 es el estándar internacional para gestión de riesgos. Proporciona principios, un marco y un proceso para gestionar el riesgo organizacional. En ComplianceOS se usa como referencia metodológica para la gestión de riesgos en los marcos core.",
      en: "ISO 31000:2018 is the international standard for risk management. Provides principles, a framework, and a process for managing organizational risk. In ComplianceOS it is used as the methodological reference for risk management across core frameworks.",
    },
    value: {
      es: [
        "Metodología ISO 31000 aplicada al registro de riesgos de todos los marcos",
        "Proceso de evaluación de riesgos: identificación, análisis, evaluación y tratamiento",
        "Integración con el catálogo de riesgos sugeridos por IA para cada framework",
        "Referencia cruzada para organizaciones que reportan bajo esquemas de riesgo ISO",
      ],
      en: [
        "ISO 31000 methodology applied to the risk register across all frameworks",
        "Risk assessment process: identification, analysis, evaluation, and treatment",
        "Integration with AI-suggested risk catalog for each framework",
        "Cross-reference for organizations reporting under ISO risk schemas",
      ],
    },
  },

  iso_22301: {
    isReference: true,
    description: {
      es: "ISO 22301:2019 es el estándar internacional para Sistemas de Gestión de Continuidad de Negocio (BCMS). Define los requisitos para planificar, establecer, implementar y mejorar la continuidad ante interrupciones. En ComplianceOS se usa como marco de referencia para cross-mapping de controles de continuidad.",
      en: "ISO 22301:2019 is the international standard for Business Continuity Management Systems (BCMS). Defines requirements for planning, establishing, implementing, and improving continuity when disruptions occur. In ComplianceOS it is used as a reference framework for cross-mapping continuity controls.",
    },
    value: {
      es: [
        "Cross-mapping de controles de continuidad ISO 27001 ↔ ISO 22301",
        "Marco de referencia para módulos de continuidad y recuperación de desastres",
        "Alineación de la gestión de incidentes y planes de continuidad al estándar",
        "Referencia para organizaciones reguladas que deben demostrar continuidad de negocio",
      ],
      en: [
        "Continuity control cross-mapping ISO 27001 ↔ ISO 22301",
        "Reference framework for continuity and disaster recovery modules",
        "Alignment of incident management and continuity plans to the standard",
        "Reference for regulated organizations required to demonstrate business continuity",
      ],
    },
  },

  // ============================================================
  // RD LATAM — NORTIC A7/A8/A9:2025 — catálogo de controles completo (ES-native)
  // ============================================================

  nortic_a7: {
    isReference: false,
    description: {
      es: "NORTIC A7:2025 — 2da edición v2.0, emitida por OGTIC — es la norma de seguridad de la información para instituciones del Gobierno Dominicano. Alinea los requisitos gubernamentales RD con ISO 27001:2022 y NIST CSF 2.0.",
      en: "NORTIC A7:2025 — 2nd edition v2.0, issued by OGTIC — is the information security standard for Dominican Government institutions. Aligns RD government requirements with ISO 27001:2022 and NIST CSF 2.0.",
    },
    value: {
      es: [
        "Catálogo completo de controles NORTIC A7 bilingüe (ES/EN) con descripciones curadas",
        "Mapeo cruzado NORTIC A7 ↔ ISO 27001:2022 para organizaciones con doble cumplimiento",
        "Análisis de brechas específico para entidades del Gobierno Dominicano",
        "Plantillas de políticas adaptadas al marco normativo RD",
        "Panel de cumplimiento NORTIC con semáforo de estados de control",
        "Único en el mercado regional: soporte nativo de NORTIC A7/A8/A9:2025",
      ],
      en: [
        "Full NORTIC A7 bilingual control catalog (ES/EN) with curated descriptions",
        "Cross-mapping NORTIC A7 ↔ ISO 27001:2022 for dual-compliance organizations",
        "Gap analysis specific to Dominican Government entities",
        "Policy templates adapted to the RD regulatory framework",
        "NORTIC compliance dashboard with control status traffic light",
        "Unique in the regional market: native support for NORTIC A7/A8/A9:2025",
      ],
    },
  },

  nortic_a8: {
    isReference: false,
    description: {
      es: "NORTIC A8:2025 — 1ra edición v1.0, emitida por OGTIC y CNCS — es la norma de ciberseguridad para el Estado Dominicano. Define los controles técnicos y organizacionales de ciberseguridad que deben cumplir las entidades gubernamentales.",
      en: "NORTIC A8:2025 — 1st edition v1.0, issued by OGTIC and CNCS — is the cybersecurity standard for the Dominican State. Defines the technical and organizational cybersecurity controls that government entities must comply with.",
    },
    value: {
      es: [
        "Catálogo de controles de ciberseguridad NORTIC A8 completo y bilingüe",
        "Mapeo cruzado NORTIC A8 ↔ NIST SCF ↔ CIS v8.1 para cobertura técnica",
        "Análisis de brechas de ciberseguridad para cumplimiento con CNCS",
        "Integración con conectores de evidencia cloud para controles técnicos A8",
        "Plantillas de planes de ciberseguridad alineadas a los requisitos NORTIC A8",
        "Puntaje de preparación para evaluaciones del CNCS",
      ],
      en: [
        "Full NORTIC A8 bilingual cybersecurity control catalog",
        "Cross-mapping NORTIC A8 ↔ NIST SCF ↔ CIS v8.1 for technical coverage",
        "Cybersecurity gap analysis for CNCS compliance",
        "Cloud evidence connector integration for A8 technical controls",
        "Cybersecurity plan templates aligned to NORTIC A8 requirements",
        "Readiness score for CNCS assessments",
      ],
    },
  },

  nortic_a9: {
    isReference: false,
    description: {
      es: "NORTIC A9:2025 — 1ra edición v1.0, emitida por OGTIC y CNCS — es la norma de gestión de riesgos y continuidad de negocio para el Estado Dominicano. Complementa A7 y A8 con el componente de continuidad y gestión de riesgos institucional.",
      en: "NORTIC A9:2025 — 1st edition v1.0, issued by OGTIC and CNCS — is the risk management and business continuity standard for the Dominican State. Complements A7 and A8 with the continuity and institutional risk management component.",
    },
    value: {
      es: [
        "Catálogo de controles de continuidad y gestión de riesgos NORTIC A9 bilingüe",
        "Registro de riesgos institucional alineado a la metodología NORTIC A9 / ISO 31000",
        "Plan de continuidad de negocio (BCP) bajo los requisitos NORTIC A9",
        "Mapeo cruzado NORTIC A9 ↔ ISO 22301 para BCP gubernamental",
        "Gestión de incidentes y acciones correctivas conforme a A9",
        "Suite NORTIC A7+A8+A9 integrada: el paquete completo de cumplimiento GRD",
      ],
      en: [
        "NORTIC A9 bilingual continuity and risk management control catalog",
        "Institutional risk register aligned to NORTIC A9 / ISO 31000 methodology",
        "Business Continuity Plan (BCP) under NORTIC A9 requirements",
        "Cross-mapping NORTIC A9 ↔ ISO 22301 for government BCP",
        "Incident management and corrective actions compliant with A9",
        "Integrated NORTIC A7+A8+A9 suite: the complete GRD compliance package",
      ],
    },
  },

  // ============================================================
  // SECTORIAL — verticales regulados RD (Banca, Telco)
  // ============================================================

  jm_18110102: {
    isReference: false,
    description: {
      es: "Reglamento JM 181101-02 (Segunda Resolución de la Junta Monetaria, 01-nov-2018) — norma de Seguridad Cibernética y de la Información obligatoria para las entidades de intermediación financiera y participantes del SIPARD en República Dominicana.",
      en: "Regulation JM 181101-02 (Second Resolution of the Monetary Board, Nov 1 2018) — the mandatory Cybersecurity and Information Security standard for financial intermediation entities and SIPARD participants in the Dominican Republic.",
    },
    value: {
      es: [
        "Catálogo completo de controles del Reglamento JM 181101-02 con cita de artículo",
        "Auditoría ISO 19011 sobre el Programa de Seguridad Cibernética y de la Información",
        "Análisis de brechas y Statement of Applicability para la Superintendencia de Bancos",
        "Preparación para supervisión: gobierno, riesgos TI, incidentes, continuidad, CSIRT sectorial",
      ],
      en: [
        "Full JM 181101-02 control catalog with article citations",
        "ISO 19011 audit over the Cybersecurity and Information Security Program",
        "Gap analysis and Statement of Applicability for the Superintendencia de Bancos",
        "Supervision readiness: governance, IT risk, incidents, continuity, sector CSIRT",
      ],
    },
  },

  // ============================================================
  // ZONA FRANCA — 6 sub-frameworks con catálogos de controles propios
  // ============================================================

  oea: {
    isReference: false,
    description: {
      es: "El Operador Económico Autorizado (OEA) es un programa de la Dirección General de Aduanas (DGA) de la República Dominicana para certificar a los operadores del comercio internacional como seguros y confiables. Incluye 20 controles en áreas de seguridad aduanera y comercial.",
      en: "The Authorized Economic Operator (OEA) is a program of the Dominican General Customs Directorate (DGA) to certify international trade operators as secure and reliable. Includes 20 controls covering customs and commercial security areas.",
    },
    value: {
      es: [
        "20 controles OEA gestionados con análisis de brechas y plan de acción",
        "Lista de verificación de cumplimiento para la certificación DGA",
        "Gestión de evidencia documental para la auditoría OEA",
        "Panel de estado por categoría de control OEA (seguridad física, TI, socios comerciales)",
        "Integración con el módulo de Zona Franca para cumplimiento consolidado",
      ],
      en: [
        "20 OEA controls managed with gap analysis and action plan",
        "Compliance checklist for DGA certification",
        "Documentary evidence management for OEA audit",
        "Status dashboard by OEA control category (physical security, IT, trading partners)",
        "Integration with the Zona Franca module for consolidated compliance",
      ],
    },
  },

  cnzfe: {
    isReference: false,
    description: {
      es: "El Reglamento 522-06 del CNZFE (Consejo Nacional de Zonas Francas de Exportación) bajo la Ley 8-90 regula los requisitos operativos para empresas en zonas francas dominicanas. Incluye 24 controles en 6 capítulos que cubren operaciones, seguridad y cumplimiento.",
      en: "CNZFE Regulation 522-06 under Law 8-90 regulates operating requirements for companies in Dominican free trade zones. Includes 24 controls in 6 chapters covering operations, security, and compliance.",
    },
    value: {
      es: [
        "24 controles CNZFE 522-06 organizados en 6 capítulos con estado de cumplimiento",
        "Análisis de brechas para renovación de licencia de zona franca",
        "Gestión de evidencia y documentación requerida por el CNZFE",
        "Checklist de inspección para auditorías del CNZFE",
        "Panel integrado con OEA para empresas de zona franca exportadoras",
      ],
      en: [
        "24 CNZFE 522-06 controls organized in 6 chapters with compliance status",
        "Gap analysis for free trade zone license renewal",
        "Evidence and documentation management required by CNZFE",
        "Inspection checklist for CNZFE audits",
        "Integrated dashboard with OEA for exporting free zone companies",
      ],
    },
  },

  iso22000: {
    isReference: false,
    description: {
      es: "ISO 22000:2018 es el estándar internacional para Sistemas de Gestión de Inocuidad Alimentaria. Define los requisitos para cualquier organización de la cadena alimentaria. Incluye 31 controles gestionados en el módulo de Zona Franca de ComplianceOS.",
      en: "ISO 22000:2018 is the international standard for Food Safety Management Systems. Defines requirements for any organization in the food chain. Includes 31 controls managed in ComplianceOS's Zona Franca module.",
    },
    value: {
      es: [
        "31 controles ISO 22000 con análisis de brechas y estado de implementación",
        "Gestión de prerrequisitos (PRP) y APPCC/HACCP integrados",
        "Documentación de inocuidad alimentaria lista para auditoría",
        "Integración con el módulo HACCP para cobertura de los 7 principios",
        "Panel de cumplimiento para industria alimentaria en zonas francas dominicanas",
      ],
      en: [
        "31 ISO 22000 controls with gap analysis and implementation status",
        "Prerequisite program (PRP) and HACCP management integrated",
        "Food safety documentation ready for audit",
        "Integration with the HACCP module for 7-principles coverage",
        "Compliance dashboard for food industry companies in Dominican free zones",
      ],
    },
  },

  bpm: {
    isReference: false,
    description: {
      es: "Buenas Prácticas de Manufactura (BPM/GMP) para la industria manufacturera en zonas francas dominicanas. ComplianceOS gestiona 42 controles BPM con perfiles diferenciados para alimentos, cosméticos y productos farmacéuticos.",
      en: "Good Manufacturing Practices (BPM/GMP) for the manufacturing industry in Dominican free trade zones. ComplianceOS manages 42 BPM controls with differentiated profiles for food, cosmetics, and pharmaceutical products.",
    },
    value: {
      es: [
        "42 controles BPM con perfiles configurables: alimentos, cosméticos, farmacéuticos",
        "Checklist de inspección BPM alineado a regulaciones MISPAS/DGA dominicanas",
        "Gestión de no conformidades y acciones correctivas BPM",
        "Documentación de procesos críticos de manufactura lista para auditoría",
        "Integración con ISO 22000 y HACCP para inocuidad alimentaria integral",
      ],
      en: [
        "42 BPM controls with configurable profiles: food, cosmetics, pharmaceutical",
        "BPM inspection checklist aligned to Dominican MISPAS/DGA regulations",
        "Non-conformity and corrective action management for BPM",
        "Critical manufacturing process documentation ready for audit",
        "Integration with ISO 22000 and HACCP for comprehensive food safety",
      ],
    },
  },

  haccp: {
    isReference: false,
    description: {
      es: "El sistema HACCP (Análisis de Peligros y Puntos Críticos de Control) es el enfoque preventivo internacionalmente reconocido para la inocuidad alimentaria. ComplianceOS gestiona los 28 controles bajo los 7 principios HACCP con planes documentados.",
      en: "HACCP (Hazard Analysis and Critical Control Points) is the internationally recognized preventive approach to food safety. ComplianceOS manages 28 controls under the 7 HACCP principles with documented plans.",
    },
    value: {
      es: [
        "28 controles HACCP organizados bajo los 7 principios con estado de documentación",
        "Plan HACCP digital: análisis de peligros, PCCs, límites críticos y medidas correctoras",
        "Gestión de registros de monitoreo de Puntos Críticos de Control",
        "Verificación y validación del sistema HACCP documentadas",
        "Integración con ISO 22000 y BPM para cumplimiento alimentario completo",
        "Trazabilidad de acciones correctivas ante desviaciones de límites críticos",
      ],
      en: [
        "28 HACCP controls organized under 7 principles with documentation status",
        "Digital HACCP plan: hazard analysis, CCPs, critical limits, and corrective measures",
        "Critical Control Point monitoring record management",
        "Documented HACCP system verification and validation",
        "Integration with ISO 22000 and BPM for complete food compliance",
        "Corrective action traceability for critical limit deviations",
      ],
    },
  },

  nordom477: {
    isReference: false,
    description: {
      es: "NORDOM 477 es la norma dominicana de calidad para productos industriales. Gestionada bajo el INDOCAL (Instituto Dominicano para la Calidad), aplica a empresas manufactureras que exportan o comercializan en el mercado dominicano. ComplianceOS gestiona 16 controles NORDOM 477.",
      en: "NORDOM 477 is the Dominican quality standard for industrial products. Managed under INDOCAL (Dominican Institute for Quality), it applies to manufacturing companies that export or commercialize in the Dominican market. ComplianceOS manages 16 NORDOM 477 controls.",
    },
    value: {
      es: [
        "16 controles NORDOM 477 con análisis de brechas y estado de cumplimiento",
        "Gestión de no conformidades y acciones correctivas de calidad",
        "Documentación técnica y de proceso alineada a los requisitos INDOCAL",
        "Integración con CNZFE para empresas de zona franca con obligaciones de calidad",
        "Panel de cumplimiento de calidad específico para mercado dominicano",
      ],
      en: [
        "16 NORDOM 477 controls with gap analysis and compliance status",
        "Quality non-conformity and corrective action management",
        "Technical and process documentation aligned to INDOCAL requirements",
        "Integration with CNZFE for free zone companies with quality obligations",
        "Quality compliance dashboard specific to the Dominican market",
      ],
    },
  },
};

/**
 * Verifica en runtime que el catálogo cubre todos los IDs de frameworks.js.
 * Llamar solo en desarrollo para detectar huérfanos.
 * En producción el build elimina este código si se importa solo en dev.
 */
export const auditCatalogCoverage = (frameworks) => {
  const missing = frameworks.filter((f) => !FRAMEWORK_CATALOG[f.id]);
  if (missing.length > 0) {
    console.warn("[frameworkCatalog] IDs sin entrada en FRAMEWORK_CATALOG:", missing.map((f) => f.id));
  }
  return missing;
};
